import {
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';

import {
  threeDSecure as braintreeThreeDSecure,
  hostedFields as braintreeHostedFields,
  Client,
  ThreeDSecure,
  HostedFields,
  HostedFieldFieldOptions,
  ThreeDSecureVerifyOptions,
  HostedFieldsTokenizePayload,
  ThreeDSecureVerifyPayload,
  BraintreeError,
} from 'braintree-web';

import { useAppSelector } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { ProvidersLibsContext } from '@/services/providersLibs/providersLibsContext';
import { PaymentSelectState } from '@/features/paymentSelect/paymentSelectSlice';
import { UserDetailsState } from '@/features/userDetails/userDetailsSlice';

const hostedFieldList = ['number', 'cvv', 'expirationDate'] as const;

const validateFields = (fields: HostedFieldFieldOptions) => (
  hostedFieldList.every((field) => {
    const fieldConfig = fields[field];
    return fieldConfig?.container;
  })
);

const initHostedFields = async (client: Client, hostedFieldsConfig: object, hostedFieldsStyles: object) => (
  braintreeHostedFields.create({
    client,
    fields: hostedFieldsConfig,
    styles: hostedFieldsStyles,
  })
);

const initThreeDSecure = async (client: Client) => (
  braintreeThreeDSecure.create({
    client,
    version: 2,
  })
);

const buildVerifyCardPayload = (
  paymentSelectState: PaymentSelectState,
  userDetailsState: UserDetailsState,
  tokenizeResult: HostedFieldsTokenizePayload,
): ThreeDSecureVerifyOptions => ({
  onLookupComplete: (_, next) => { next(); },
  nonce: tokenizeResult.nonce,
  amount: paymentSelectState.amount.toString(),
  bin: tokenizeResult.details.bin,
  collectDeviceData: true,
  email: userDetailsState.email,
  billingAddress: {
    givenName: userDetailsState.firstName,
    surname: userDetailsState.lastName,
    streetAddress: userDetailsState.address1,
    extendedAddress: userDetailsState.address2,
    line3: userDetailsState.address3,
    locality: userDetailsState.town,
    region: '',
    postalCode: userDetailsState.postcode,
    countryCodeAlpha2: userDetailsState.country,
  },
  additionalInformation: {
    shippingGivenName: userDetailsState.firstName,
    shippingSurname: userDetailsState.lastName,
    shippingAddress: {
      streetAddress: userDetailsState.address1,
      extendedAddress: userDetailsState.address2,
      line3: userDetailsState.address3,
      locality: userDetailsState.town,
      region: '',
      postalCode: userDetailsState.postcode,
      countryCodeAlpha2: userDetailsState.country,
    },
  },
});

const setupValidityChange = (hostedFieldsInstance: HostedFields, setFormValid: (formValid: boolean) => void) => {
  hostedFieldsInstance?.on('validityChange', (event: { fields: Record<string, { isValid: boolean }> }) => {
    if (setFormValid) {
      setFormValid(false);
    }

    const stateValid = Object.values(event.fields).every(field => (
      field?.isValid
    ));

    if (setFormValid) {
      setFormValid(stateValid);
    }
  });
};

export function useBraintreePayment(
  hostedFieldsConfig: object,
  hostedFieldsStyles: object,
  setFormValid: (formValid: boolean) => void,
  initCallbacks?: {
    onSuccess?: () => void,
    onError?: (error?: unknown) => void,
  }
) {
  const onSuccess = useRef<(() => void) | undefined>(undefined);
  const onError = useRef<((error?: unknown) => void) | undefined>(undefined);
  onSuccess.current = initCallbacks?.onSuccess;
  onError.current = initCallbacks?.onError;

  const paymentSelectState = useAppSelector(state => state.paymentSelect);
  const userDetailsState = useAppSelector(state => state.userDetails);

  const providersLibs = useContext(ProvidersLibsContext);

  const [threeDSecure, setThreeDSecure] = useState<ThreeDSecure>();
  const [hostedFields, setHostedFields] = useState<HostedFields>();

  // Load Braintree threeDSecure instance on component load
  useEffect(() => {
    async function loadBraintree() {
      if (
        !validateFields(hostedFieldsConfig)  // Wait until hostedFields containers are rendered
        || !providersLibs?.braintreeClient   // Wait until Braintree client is available before loading threeDSecure
      ) {
        return;
      }

      if (!threeDSecure) {
        try {
          const threeDSecureInstance = await initThreeDSecure(providersLibs.braintreeClient);
          setThreeDSecure(threeDSecureInstance);
        } catch (error) {
          logger.error('Error initialising Braintree threeDSecure');
          logger.error(error);
          if (onError.current) {
            onError.current(error);
          }
          return;
        }
      }

      try {
        const hostedFieldsInstance = await initHostedFields(providersLibs.braintreeClient, hostedFieldsConfig, hostedFieldsStyles);
        setupValidityChange(hostedFieldsInstance, setFormValid);
        setHostedFields(hostedFieldsInstance);
      } catch (error) {
        // We could use hostedFields.teardown() to remove/re-create hostedFields, but retaining an existing instance has shown to be reliable
        // If we find problems with this approach, we can add a teardown (returned from the parent useEffect())
        if ((error as BraintreeError)?.code !== 'HOSTED_FIELDS_FIELD_DUPLICATE_IFRAME') {
          logger.error('Error initialising Braintree hostedFields');
          logger.error(error);
          if (onError.current) {
            onError.current(error);
          }
          return;
        }
      }

      if (!!threeDSecure && !!hostedFields && onSuccess.current) {
        onSuccess.current();
      }
    }
    void loadBraintree();
  }, [
    providersLibs?.braintreeClient,
    threeDSecure,
    hostedFields,
    hostedFieldsConfig,
    hostedFieldsStyles,
    setFormValid,
    initCallbacks,
  ]);

  const triggerPayment = useCallback(async (
    callbacks?: {
      onSuccess?: (threeDSecureResult: ThreeDSecureVerifyPayload, tokenizeResult: HostedFieldsTokenizePayload) => void,
      onError?: (error?: unknown) => void,
    }
  ) => {
    if (!threeDSecure || !hostedFields) {
      logger.error('Error initialising Braintree threeDSecure or hostedFields');
      if (callbacks?.onError) {
        callbacks.onError(new Error('Error initialising Braintree threeDSecure or hostedFields'));
      }
      return;
    }

    // API Reference: https://braintree.github.io/braintree-web/current/HostedFields.html#tokenize
    // Example: https://developer.paypal.com/braintree/docs/guides/hosted-fields/setup-and-integration/javascript/v3/
    let tokenizeResult;
    try {
      tokenizeResult = await hostedFields.tokenize();
    } catch (error) {
      logger.error('Braintree hostedFields error tokenizing');
      if (callbacks?.onError) {
        callbacks.onError(new Error('Braintree hostedFields error tokenizing'));
      }
      return;
    }

    // API Reference: https://braintree.github.io/braintree-web/current/ThreeDSecure.html#verifyCard
    // Example: https://developer.paypal.com/braintree/docs/guides/3d-secure/client-side/javascript/v3/#hosted-fields
    let threeDSecureResult;
    try {
      const payload = buildVerifyCardPayload(paymentSelectState, userDetailsState, tokenizeResult);
      threeDSecureResult = await threeDSecure.verifyCard(payload);
    } catch (error) {
      logger.error('Braintree 3DS/SCA error verifying card');
      if (callbacks?.onError) {
        callbacks.onError(new Error('Braintree 3DS/SCA error verifying card'));
      }
      return;
    }

    if (callbacks?.onSuccess) {
      callbacks.onSuccess(threeDSecureResult, tokenizeResult);
    }
  }, [threeDSecure, hostedFields, paymentSelectState, userDetailsState]);

  return {
    triggerPayment,
  };
}
